import { atom, type WritableAtom } from 'nanostores';
import { type ActiveDocument } from '~/types';

type ShowUnsavedChangesModal = WritableAtom<boolean>;
export type NavigationState = { to: string; params?: Record<string, string>; search?: Record<string, unknown> };
export type PendingNavigation = WritableAtom<NavigationState | null>;

export type DeploymentNotificationType =
  | 'success'
  | 'error'
  | 'fixDeploymentErrors'
  | 'streamError'
  | 'warning';

export type DeploymentNotification = {
  type: DeploymentNotificationType;
  content: string;
  versionName?: string;
};

export type ThemePreference = 'light' | 'dark' | 'system';

export class InterfaceStore {
  showUnsavedChangesModal: ShowUnsavedChangesModal = atom<boolean>(false);
  showLeaveWhileStreamingModal: WritableAtom<boolean> = atom<boolean>(false);
  fixDeploymentAttempts: WritableAtom<number> = atom<number>(0);
  pendingNavigation: PendingNavigation = atom<NavigationState | null>(null);
  isDeploying: WritableAtom<boolean> = atom<boolean>(false);
  buildIsStreaming: WritableAtom<boolean> = atom<boolean>(false);
  showExpiredSessionModal: WritableAtom<boolean> = atom<boolean>(false);
  // Resolved appearance actually applied to the UI. Always 'light' or 'dark' so
  // every consumer stays unaffected by the 'system' preference.
  appearanceMode: WritableAtom<'light' | 'dark'> = atom<'light' | 'dark'>('dark');
  // The user's chosen preference. 'system' follows the OS colour scheme and keeps
  // `appearanceMode` in sync when the OS setting changes.
  themePreference: WritableAtom<ThemePreference> = atom<ThemePreference>('system');
  private systemThemeQuery: MediaQueryList | null = null;
  documentViewer: WritableAtom<boolean> = atom<boolean>(false);
  // v2 build agent surfaces deployment lifecycle as UI banners; v1 keeps using
  // buildChatStore. These atoms are written by useDeploy / deploymentErrors and
  // read by v2 BaseCodeChat.
  deploymentNotification: WritableAtom<DeploymentNotification | null> = atom<DeploymentNotification | null>(null);
  paymentRequired: WritableAtom<boolean> = atom<boolean>(false);
  activeDocument: WritableAtom<{
    type: 'file' | 'workflow';
    document: ActiveDocument | null;
  }> = atom<{
    type: 'file' | 'workflow';
    document: ActiveDocument | null;
  }>({ type: 'file', document: null });

  constructor() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemThemeQuery.addEventListener('change', this.handleSystemThemeChange);
    }
    // No stored value (or a value from before the 'system' option existed that is
    // no longer one of the valid keys) falls back to following the system theme.
    const stored = localStorage.getItem('versoriAppearanceMode') as ThemePreference | null;
    const preference: ThemePreference =
      stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
    this.applyThemePreference(preference);
  }

  private handleSystemThemeChange = (event: MediaQueryListEvent) => {
    if (this.themePreference.get() === 'system') {
      this.appearanceMode.set(event.matches ? 'dark' : 'light');
    }
  };

  private resolveSystemAppearance(): 'light' | 'dark' {
    return this.systemThemeQuery?.matches ? 'dark' : 'light';
  }

  private applyThemePreference(preference: ThemePreference) {
    this.themePreference.set(preference);
    this.appearanceMode.set(preference === 'system' ? this.resolveSystemAppearance() : preference);
  }

  setActiveDocument(type: 'file' | 'workflow', document: ActiveDocument | null) {
    this.activeDocument.set({ type, document });
  }

  toggleDocumentViewer(value: boolean) {
    this.documentViewer.set(value);
  }

  setShowUnsavedChangesModal(value: boolean) {
    this.showUnsavedChangesModal.set(value);
  }

  setShowLeaveWhileStreamingModal(value: boolean) {
    this.showLeaveWhileStreamingModal.set(value);
  }

  incrementFixDeploymentAttempts() {
    this.fixDeploymentAttempts.set(this.fixDeploymentAttempts.get() + 1);
  }

  resetFixDeploymentAttempts() {
    this.fixDeploymentAttempts.set(0);
  }

  setIsDeploying(value: boolean) {
    this.isDeploying.set(value);
  }

  toggleIsStreaming(value: boolean) {
    this.buildIsStreaming.set(value);
  }

  setPendingNavigation(value: NavigationState | null) {
    this.pendingNavigation.set(value);
  }

  setShowExpiredSessionModal(value: boolean) {
    this.showExpiredSessionModal.set(value);
  }

  setAppearanceMode(value: ThemePreference) {
    this.applyThemePreference(value);
    localStorage.setItem('versoriAppearanceMode', value);
  }

  setDeploymentNotification(value: DeploymentNotification | null) {
    this.deploymentNotification.set(value);
  }

  clearDeploymentNotification() {
    this.deploymentNotification.set(null);
  }

  setPaymentRequired(value: boolean) {
    this.paymentRequired.set(value);
  }

  reset() {
    this.resetFixDeploymentAttempts();
    this.setIsDeploying(false);
    this.toggleIsStreaming(false);
    this.setShowExpiredSessionModal(false);
    this.clearDeploymentNotification();
    this.setPaymentRequired(false);
    this.setShowLeaveWhileStreamingModal(false);
  }
}

export const interfaceStore = new InterfaceStore();
